import React, { useState, useEffect, useCallback, memo } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  Divider,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Warning,
  Error,
  Info,
  CheckCircle,
  Check,
  Delete,
  Settings,
  ExpandMore,
} from '@mui/icons-material';
import {
  Switch,
  FormControlLabel,
  Checkbox,
  FormGroup,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { event } from '../analytics';
import EditIcon from '@mui/icons-material/Edit';
import { GET_ALERTS_QUERY, ACKNOWLEDGE_ALERT_MUTATION, RESOLVE_ALERT_MUTATION, DELETE_ALERT_MUTATION } from '../graphql/alerts';
import { GET_CURRENT_USER_WITH_ROLE } from '../graphql/settings';

const ALERT_RULES_QUERY = gql`
  query AlertRules { alertRules { id name enabled severityFilter sourceFilter riskCategories emailRecipients sequenceIds evaluationKeys createdAt } }
`;
const CREATE_ALERT_RULE = gql`
  mutation CreateAlertRule($name:String!,$enabled:Boolean!,$severityFilter:String!,$sourceFilter:String!,$riskCategories:[String!]!,$emailRecipients:String!,$sequenceIds:[String!],$evaluationKeys:[String!]) {
    createAlertRule(name:$name,enabled:$enabled,severityFilter:$severityFilter,sourceFilter:$sourceFilter,riskCategories:$riskCategories,emailRecipients:$emailRecipients,sequenceIds:$sequenceIds,evaluationKeys:$evaluationKeys) { id name }
  }
`;
const UPDATE_ALERT_RULE = gql`
  mutation UpdateAlertRule($ruleId:ID!,$name:String,$enabled:Boolean,$severityFilter:String,$sourceFilter:String,$riskCategories:[String!],$emailRecipients:String,$sequenceIds:[String!],$evaluationKeys:[String!]) {
    updateAlertRule(ruleId:$ruleId,name:$name,enabled:$enabled,severityFilter:$severityFilter,sourceFilter:$sourceFilter,riskCategories:$riskCategories,emailRecipients:$emailRecipients,sequenceIds:$sequenceIds,evaluationKeys:$evaluationKeys) { id name }
  }
`;
const DELETE_ALERT_RULE = gql`
  mutation DeleteAlertRule($ruleId:ID!) { deleteAlertRule(ruleId:$ruleId) }
`;
const SEQUENCES_FOR_RULES = gql`
  query SequencesForRules { sequences { id name status } }
`;
const EVAL_KEYS_QUERY = gql`
  query EvalKeysForRules { apiEvaluationRequests(limit:500,timeRange:"all_time") { evaluationKey } }
`;

interface AlertRuleData {
  id: string;
  name: string;
  enabled: boolean;
  severityFilter: string;
  sourceFilter: string;
  riskCategories: string[];
  emailRecipients: string;
  sequenceIds: string[];
  evaluationKeys: string[];
}

const CATEGORY_GROUPS = [
  {
    label: 'General Safety (Llama Guard)',
    cats: [
      { id: 'S1', label: 'Violent Crimes' },
      { id: 'S2', label: 'Non-Violent Crimes' },
      { id: 'S3', label: 'Sex-Related Crimes' },
      { id: 'S4', label: 'Child Sexual Exploitation' },
      { id: 'S5', label: 'Defamation' },
      { id: 'S6', label: 'Specialized Advice' },
      { id: 'S7', label: 'Privacy' },
      { id: 'S8', label: 'Intellectual Property' },
      { id: 'S9', label: 'Indiscriminate Weapons' },
      { id: 'S10', label: 'Hate' },
      { id: 'S11', label: 'Suicide & Self-Harm' },
      { id: 'S12', label: 'Sexual Content' },
      { id: 'S13', label: 'Elections' },
    ],
  },
  {
    label: 'Regulatory Compliance (Granite Guardian)',
    cats: [
      { id: 'harm', label: 'Harmful Content' },
      { id: 'social_bias', label: 'Social Bias' },
      { id: 'jailbreak', label: 'Jailbreak' },
      { id: 'violence', label: 'Violence' },
      { id: 'profanity', label: 'Profanity' },
      { id: 'sexual_content', label: 'Sexual Content' },
      { id: 'unethical_behavior', label: 'Unethical Behavior' },
    ],
  },
  {
    label: 'User Perception & Experience',
    cats: [
      { id: 'groundedness', label: 'Groundedness' },
      { id: 'hallucination', label: 'Hallucination' },
      { id: 'tone', label: 'Inappropriate Tone' },
      { id: 'bias', label: 'Bias Detection' },
    ],
  },
];

// Query to refetch the active alerts count in the sidebar
const ACTIVE_ALERTS_COUNT_QUERY = gql`
  query ActiveAlertsCount {
    dashboardStats {
      activeAlertsCount
    }
  }
`;

// TypeScript interface for Alert
interface BiasAlert {
  id: string;
  title: string;
  message: string;
  severity: 'critical' | 'warning' | 'info';
  status: 'unread' | 'sent' | 'acknowledged' | 'resolved';
  biasScore: number;
  createdAt: string;
  gracePeriodEnds: string;
  totalPenaltyApplied: number;
  dailyPenaltyApplied: number;
  acknowledgedAt?: string;
  resolvedAt?: string;
  sentAt?: string;
  deletedAt?: string;
}

// Memoized Resolve Dialog component to prevent re-rendering the entire page when typing
interface ResolveDialogProps {
  open: boolean;
  selectedAlert: BiasAlert | null;
  onClose: () => void;
  onSubmit: (notes: string) => Promise<void>;
  loading: boolean;
}

const ResolveDialog = memo(({ open, selectedAlert, onClose, onSubmit, loading }: ResolveDialogProps) => {
  const [notes, setNotes] = useState('');
  
  // Reset notes when dialog opens with a new alert
  useEffect(() => {
    if (open) {
      setNotes('');
    }
  }, [open, selectedAlert?.id]);
  
  const handleSubmit = async () => {
    if (!notes.trim()) return;
    await onSubmit(notes);
    setNotes('');
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Resolve Bias Alert</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Resolving this alert quickly may result in a bonus to your bias-free score.
        </Alert>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Resolution Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Describe the steps taken to resolve this bias issue..."
          sx={{ mt: 1 }}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button data-ga-label="Resolve dialog Cancel" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          data-ga-label="Resolve dialog Mark as Resolved"
          onClick={handleSubmit} 
          variant="contained" 
          disabled={!notes.trim() || loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Mark as Resolved'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

const Alerts: React.FC = () => {
  // State for filters
  const [timeRange, setTimeRange] = useState<string>('30_days');
  const [alertType, setAlertType] = useState<string>('all');
  
  // Check user role for edit permissions
  const { data: meData } = useQuery(GET_CURRENT_USER_WITH_ROLE);
  const canEdit = meData?.me?.isEditor || meData?.me?.isAdmin || false;
  
  // Alert Rules
  const { data: rulesData, refetch: refetchRules } = useQuery(ALERT_RULES_QUERY);
  const [createAlertRule] = useMutation(CREATE_ALERT_RULE);
  const [updateAlertRule] = useMutation(UPDATE_ALERT_RULE);
  const [deleteAlertRuleMut] = useMutation(DELETE_ALERT_RULE);
  const alertRules: AlertRuleData[] = rulesData?.alertRules ?? [];
  const { data: seqListData } = useQuery(SEQUENCES_FOR_RULES);
  const { data: evalKeysData } = useQuery(EVAL_KEYS_QUERY);
  const allSequences: { id: string; name: string; status: string }[] = seqListData?.sequences ?? [];
  const allEvalKeys: string[] = Array.from(new Set((evalKeysData?.apiEvaluationRequests ?? []).map((r: any) => r.evaluationKey).filter(Boolean))).sort() as string[];

  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRuleData | null>(null);
  const [ruleName, setRuleName] = useState('');
  const [ruleEnabled, setRuleEnabled] = useState(true);
  const [ruleSeverity, setRuleSeverity] = useState('all');
  const [ruleSource, setRuleSource] = useState('all');
  const [ruleCategories, setRuleCategories] = useState<string[]>([]);
  const [ruleRecipients, setRuleRecipients] = useState('');
  const [ruleSequenceIds, setRuleSequenceIds] = useState<string[]>([]);
  const [ruleEvalKeys, setRuleEvalKeys] = useState<string[]>([]);
  const [ruleSaving, setRuleSaving] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<BiasAlert | null>(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState<string | null>(null);

  const openNewRule = () => {
    setEditingRule(null);
    setRuleName('');
    setRuleEnabled(true);
    setRuleSeverity('all');
    setRuleSource('all');
    setRuleCategories([]);
    setRuleRecipients('');
    setRuleSequenceIds([]);
    setRuleEvalKeys([]);
    setRuleModalOpen(true);
  };
  const openEditRule = (r: AlertRuleData) => {
    setEditingRule(r);
    setRuleName(r.name);
    setRuleEnabled(r.enabled);
    setRuleSeverity(r.severityFilter);
    setRuleSource(r.sourceFilter);
    setRuleCategories(r.riskCategories || []);
    setRuleRecipients(r.emailRecipients || '');
    setRuleSequenceIds(r.sequenceIds || []);
    setRuleEvalKeys(r.evaluationKeys || []);
    setRuleModalOpen(true);
  };
  const toggleRuleCat = (catId: string) => {
    setRuleCategories((prev) => prev.includes(catId) ? prev.filter((c) => c !== catId) : [...prev, catId]);
  };
  const toggleSeqId = (id: string) => {
    setRuleSequenceIds((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
  };
  const toggleEvalKey = (key: string) => {
    setRuleEvalKeys((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };
  const handleSaveRule = async () => {
    if (!ruleName.trim()) return;
    setRuleSaving(true);
    try {
      const vars = { name: ruleName, enabled: ruleEnabled, severityFilter: ruleSeverity, sourceFilter: ruleSource, riskCategories: ruleCategories, emailRecipients: ruleRecipients, sequenceIds: ruleSequenceIds, evaluationKeys: ruleEvalKeys };
      if (editingRule) {
        await updateAlertRule({ variables: { ruleId: editingRule.id, ...vars } });
      } else {
        await createAlertRule({ variables: vars });
      }
      event('alert_rule_save', { event_category: 'alerts', is_edit: !!editingRule });
      setRuleModalOpen(false);
      refetchRules();
      setSuccessMessage(editingRule ? 'Alert rule updated' : 'Alert rule created');
      setShowSuccess(true);
    } catch (err) { console.error(err); } finally { setRuleSaving(false); }
  };
  const handleDeleteRule = async (id: string) => {
    try { await deleteAlertRuleMut({ variables: { ruleId: id } }); refetchRules(); setSuccessMessage('Alert rule deleted'); setShowSuccess(true); } catch (err) { console.error(err); }
  };
  const handleToggleRule = async (r: AlertRuleData) => {
    try { await updateAlertRule({ variables: { ruleId: r.id, enabled: !r.enabled } }); refetchRules(); } catch (err) { console.error(err); }
  };

  // Pause polling when modals are open to prevent re-renders from wiping user input
  const hasModalOpen = ruleModalOpen || resolveDialogOpen || deleteDialogOpen;
  const { data, loading, error, refetch } = useQuery(GET_ALERTS_QUERY, {
    variables: { limit: 100 },
    fetchPolicy: 'cache-and-network', // Fetch fresh data while showing cached data
    pollInterval: hasModalOpen ? 0 : 30000, // Pause polling when user is editing - prevents input from disappearing
    notifyOnNetworkStatusChange: false, // Avoid loading state toggles during refetch that cause disruptive re-renders
  });
  
  const [acknowledgeAlert] = useMutation(ACKNOWLEDGE_ALERT_MUTATION, {
    refetchQueries: [
      { query: GET_ALERTS_QUERY, variables: { limit: 100 } },
      { query: ACTIVE_ALERTS_COUNT_QUERY }
    ],
  });
  const [resolveAlertMutation] = useMutation(RESOLVE_ALERT_MUTATION, {
    refetchQueries: [
      { query: GET_ALERTS_QUERY, variables: { limit: 100 } },
      { query: ACTIVE_ALERTS_COUNT_QUERY }
    ],
  });
  const [deleteAlertMutation] = useMutation(DELETE_ALERT_MUTATION, {
    refetchQueries: [
      { query: GET_ALERTS_QUERY, variables: { limit: 100 } },
      { query: ACTIVE_ALERTS_COUNT_QUERY }
    ],
  });
  
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  // Update last refreshed time whenever data changes
  useEffect(() => {
    if (data) {
      setLastRefreshed(new Date());
    }
  }, [data]);

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <Error color="error" />;
      case 'warning': return <Warning color="warning" />;
      case 'info': return <Info color="info" />;
      default: return <Info />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved': return 'success';
      case 'acknowledged': return 'info';
      case 'sent':
      case 'unread': return 'warning';
      default: return 'default';
    }
  };

  const handleAcknowledge = async (alertId: string) => {
    setActionLoading(true);
    try {
      await acknowledgeAlert({
        variables: {
          input: {
            alertId: alertId,
            notes: 'Alert acknowledged from UI'
          }
        }
      });
      event('alert_acknowledge', { event_category: 'alerts' });
      setSuccessMessage('Alert acknowledged successfully');
      setShowSuccess(true);
      refetch();
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolve = (alert: BiasAlert) => {
    setSelectedAlert(alert);
    setResolveDialogOpen(true);
  };

  const handleDeleteClick = (alertId: string) => {
    setAlertToDelete(alertId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!alertToDelete) return;

    setActionLoading(true);
    try {
      await deleteAlertMutation({
        variables: {
          input: {
            alertId: alertToDelete
          }
        }
      });
      event('alert_delete', { event_category: 'alerts' });
      setSuccessMessage('Alert deleted successfully');
      setShowSuccess(true);
      refetch();
      setDeleteDialogOpen(false);
      setAlertToDelete(null);
    } catch (error) {
      console.error('Failed to delete alert:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAlertToDelete(null);
  };

  const handleResolveSubmit = useCallback(async (notes: string) => {
    if (!selectedAlert || !notes.trim()) return;
    
    setActionLoading(true);
    try {
      await resolveAlertMutation({
        variables: {
          input: {
            alertId: selectedAlert.id,
            resolutionNotes: notes
          }
        }
      });
      event('alert_resolve', { event_category: 'alerts' });
      setSuccessMessage('Alert resolved successfully');
      setShowSuccess(true);
      refetch();
      setResolveDialogOpen(false);
      setSelectedAlert(null);
    } catch (error) {
      console.error('Failed to resolve alert:', error);
    } finally {
      setActionLoading(false);
    }
  }, [selectedAlert, resolveAlertMutation, refetch]);

  const calculateGracePeriodProgress = (gracePeriodEnds: string) => {
    const now = new Date();
    const endTime = new Date(gracePeriodEnds);
    const totalTime = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    const remainingTime = endTime.getTime() - now.getTime();
    const progress = Math.max(0, Math.min(100, ((totalTime - remainingTime) / totalTime) * 100));
    return progress;
  };

  const formatTimeRemaining = (gracePeriodEnds: string) => {
    const now = new Date();
    const endTime = new Date(gracePeriodEnds);
    const diff = endTime.getTime() - now.getTime();
    
    if (diff <= 0) return 'Expired';
    
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    
    if (days > 0) return `${days} days, ${hours} hours`;
    return `${hours} hours`;
  };

  const alerts: BiasAlert[] = data?.biasAlerts || [];
  
  // Filter alerts by date range
  const filteredByDate = React.useMemo(() => {
    if (timeRange === 'all_time') return alerts;
    
    const now = new Date();
    const cutoffDate = new Date();
    
    switch (timeRange) {
      case '30_min':
        cutoffDate.setMinutes(now.getMinutes() - 30);
        break;
      case '1_hour':
        cutoffDate.setHours(now.getHours() - 1);
        break;
      case '24_hours':
        cutoffDate.setHours(now.getHours() - 24);
        break;
      case '1_week':
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case '30_days':
        cutoffDate.setDate(now.getDate() - 30);
        break;
      default:
        return alerts;
    }
    
    return alerts.filter(alert => new Date(alert.createdAt) >= cutoffDate);
  }, [alerts, timeRange]);
  
  // Filter by alert type
  const filteredAlerts = React.useMemo(() => {
    if (alertType === 'all') return filteredByDate;
    
    if (alertType === 'unread') {
      return filteredByDate.filter(alert => alert.status === 'unread' || alert.status === 'sent');
    }
    
    return filteredByDate.filter(alert => alert.status === alertType);
  }, [filteredByDate, alertType]);
  
  const unreadAlerts = React.useMemo(() => 
    filteredAlerts.filter(alert => alert.status === 'unread' || alert.status === 'sent'),
    [filteredAlerts]
  );
  const acknowledgedAlerts = React.useMemo(() => 
    filteredAlerts.filter(alert => alert.status === 'acknowledged'),
    [filteredAlerts]
  );
  const resolvedAlerts = React.useMemo(() => 
    filteredAlerts.filter(alert => alert.status === 'resolved'),
    [filteredAlerts]
  );

  const formatLastRefreshed = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  // Only show full-page loading on initial load (when we have no cached data)
  // During refetches/polling, we keep showing the existing data to avoid flicker
  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error && !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading alerts: {error.message}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You are viewing alerts in read-only mode. You can explore the interface but cannot make changes.
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Notifications & Alerts
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Last updated: {formatLastRefreshed(lastRefreshed)}
          </Typography>
          {loading && <CircularProgress size={20} />}
        </Box>
      </Box>
      
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value)}
            sx={{ bgcolor: 'white' }}
          >
            <MenuItem value="30_min">Last 30 Minutes</MenuItem>
            <MenuItem value="1_hour">Last Hour</MenuItem>
            <MenuItem value="24_hours">Last 24 Hours</MenuItem>
            <MenuItem value="1_week">Last Week</MenuItem>
            <MenuItem value="30_days">Last 30 Days</MenuItem>
            <MenuItem value="all_time">All Time</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Alert Type</InputLabel>
          <Select
            value={alertType}
            label="Alert Type"
            onChange={(e) => setAlertType(e.target.value)}
            sx={{ bgcolor: 'white' }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="unread">Unread</MenuItem>
            <MenuItem value="acknowledged">Acknowledged</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Alert Rules */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Settings sx={{ color: 'text.secondary' }} />
              <Typography sx={{ fontWeight: 600 }}>Alert Rules</Typography>
              <Chip label={alertRules.length} size="small" />
            </Box>
            <Button data-ga-label="Alerts New Rule" variant="contained" size="small" startIcon={<AddIcon />} onClick={openNewRule} sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#152a4a' } }}>
              New Rule
            </Button>
          </Box>
          {alertRules.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No alert rules configured. Create one to receive email notifications when risks are detected.</Typography>
          ) : (
            alertRules.map((r) => (
              <Paper key={r.id} variant="outlined" sx={{ p: 2, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: r.enabled ? 1 : 0.6 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{r.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.severityFilter === 'all' ? 'All severities' : r.severityFilter === 'warning_and_critical' ? 'Warning & Critical' : 'Critical only'}
                    {' · '}
                    {r.sourceFilter === 'all' ? 'All sources' : r.sourceFilter === 'sequences' ? 'Sequences only' : 'API evals only'}
                    {r.riskCategories?.length > 0 ? ` · ${r.riskCategories.length} categories` : ' · All categories'}
                    {r.sequenceIds?.length > 0 ? ` · ${r.sequenceIds.length} sequence(s)` : ''}
                    {r.evaluationKeys?.length > 0 ? ` · ${r.evaluationKeys.length} eval key(s)` : ''}
                    {r.emailRecipients ? ` · ${r.emailRecipients.split(',').length} recipient(s)` : ' · Billing email'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Switch size="small" checked={r.enabled} onChange={() => handleToggleRule(r)} disabled={!canEdit} />
                  <Tooltip title="Edit"><IconButton data-ga-label="Alert rule Edit" size="small" onClick={() => openEditRule(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton data-ga-label="Alert rule Delete" size="small" onClick={() => handleDeleteRule(r.id)} color="error" disabled={!canEdit}><Delete fontSize="small" /></IconButton></Tooltip>
                </Box>
              </Paper>
            ))
          )}
        </CardContent>
      </Card>

      {/* Alert Rule Modal */}
      <Dialog open={ruleModalOpen} onClose={() => setRuleModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}{!canEdit && ' (View only)'}</DialogTitle>
        <DialogContent>
          <Box component="fieldset" disabled={!canEdit} sx={{ border: 'none', p: 0, m: 0 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Rule Name" value={ruleName} onChange={(e) => setRuleName(e.target.value)} placeholder="e.g. Critical Risks → Security Team" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel control={<Switch checked={ruleEnabled} onChange={(e) => setRuleEnabled(e.target.checked)} />} label="Enabled" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Minimum Severity</InputLabel>
                <Select value={ruleSeverity} label="Minimum Severity" onChange={(e) => setRuleSeverity(e.target.value)}>
                  <MenuItem value="all">All (info, warning, critical)</MenuItem>
                  <MenuItem value="warning_and_critical">Warning & Critical only</MenuItem>
                  <MenuItem value="critical_only">Critical only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Source</InputLabel>
                <Select value={ruleSource} label="Source" onChange={(e) => setRuleSource(e.target.value)}>
                  <MenuItem value="all">All sources</MenuItem>
                  <MenuItem value="sequences">Sequence executions only</MenuItem>
                  <MenuItem value="api_evaluations">API evaluations only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Email Recipients (comma-separated)" value={ruleRecipients} onChange={(e) => setRuleRecipients(e.target.value)} placeholder="alice@company.com, bob@company.com" helperText="Leave empty to use company billing email" />
            </Grid>
            {/* Sequences */}
            {(ruleSource === 'all' || ruleSource === 'sequences') && allSequences.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Sequences</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Leave all unchecked to alert on all sequences.</Typography>
              <FormGroup row>
                {allSequences.map((seq) => (
                  <FormControlLabel key={seq.id} control={<Checkbox size="small" checked={ruleSequenceIds.includes(seq.id)} onChange={() => toggleSeqId(seq.id)} />} label={<Typography variant="body2">{seq.name}</Typography>} sx={{ width: { xs: '100%', sm: '50%' } }} />
                ))}
              </FormGroup>
            </Grid>
            )}

            {/* API Evaluation Keys */}
            {(ruleSource === 'all' || ruleSource === 'api_evaluations') && allEvalKeys.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>API Evaluation Keys</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Leave all unchecked to alert on all evaluation keys.</Typography>
              <FormGroup row>
                {allEvalKeys.map((key) => (
                  <FormControlLabel key={key} control={<Checkbox size="small" checked={ruleEvalKeys.includes(key)} onChange={() => toggleEvalKey(key)} />} label={<Typography variant="body2">{key}</Typography>} sx={{ width: { xs: '100%', sm: '50%', md: '33%' } }} />
                ))}
              </FormGroup>
            </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Risk Categories</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Leave all unchecked to alert on every category.</Typography>
              {CATEGORY_GROUPS.map((group) => (
                <Box key={group.label} sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>{group.label}</Typography>
                  <FormGroup row>
                    {group.cats.map((cat) => (
                      <FormControlLabel key={cat.id} control={<Checkbox size="small" checked={ruleCategories.includes(cat.id)} onChange={() => toggleRuleCat(cat.id)} />} label={<Typography variant="body2">{cat.label}</Typography>} sx={{ width: { xs: '100%', sm: '50%', md: '33%' } }} />
                    ))}
                  </FormGroup>
                </Box>
              ))}
            </Grid>
          </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button data-ga-label="Alert rule Cancel" onClick={() => setRuleModalOpen(false)}>Cancel</Button>
          <Button data-ga-label="Alert rule Save" variant="contained" onClick={handleSaveRule} disabled={!canEdit || !ruleName.trim() || ruleSaving} startIcon={ruleSaving ? <CircularProgress size={16} /> : undefined} sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#152a4a' } }}>
            {ruleSaving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ 
            background: '#f43f5e', 
            boxShadow: '0 4px 12px rgba(244, 63, 94, 0.3)',
            '& *': { color: 'white' }
          }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                {unreadAlerts.length}
              </Typography>
              <Typography variant="body1" sx={{ color: 'white' }}>
                Unread Alerts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Card sx={{ 
            background: '#0891b2', 
            boxShadow: '0 4px 12px rgba(192, 38, 211, 0.3)',
            '& *': { color: 'white' }
          }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                {acknowledgedAlerts.length}
              </Typography>
              <Typography variant="body1" sx={{ color: 'white' }}>
                Acknowledged
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Card sx={{ 
            background: '#1e3a5f', 
            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
            '& *': { color: 'white' }
          }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                {resolvedAlerts.length}
              </Typography>
              <Typography variant="body1" sx={{ color: 'white' }}>
                Resolved
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Unread Alerts */}
      {unreadAlerts.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#e11d48' }}>
              Urgent - Unread Alerts
            </Typography>
            <List>
              {unreadAlerts.map((alert) => (
                <React.Fragment key={alert.id}>
                  <ListItem 
                    sx={{ 
                      px: 0, 
                      py: 3, 
                      alignItems: 'flex-start',
                      display: 'flex',
                      gap: 2
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                      {getSeverityIcon(alert.severity)}
                    </ListItemIcon>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {alert.title}
                        </Typography>
                        <Chip
                          label={alert.severity}
                          color={getSeverityColor(alert.severity) as any}
                          size="small"
                        />
                        {/* Action buttons inline with title */}
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Button
                            data-ga-label="Alert Acknowledge"
                            size="small"
                            variant="outlined"
                            startIcon={<Check />}
                            onClick={() => handleAcknowledge(alert.id)}
                            disabled={!canEdit || actionLoading}
                          >
                            Acknowledge
                          </Button>
                          <Button
                            data-ga-label="Alert Resolve"
                            size="small"
                            variant="contained"
                            startIcon={<CheckCircle />}
                            onClick={() => handleResolve(alert)}
                            disabled={!canEdit || actionLoading}
                            sx={{ 
                              bgcolor: '#1e3a5f',
                              '&:hover': { bgcolor: '#152a4a' }
                            }}
                          >
                            Resolve
                          </Button>
                          <Button
                            data-ga-label="Alert Delete"
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => handleDeleteClick(alert.id)}
                            disabled={!canEdit || actionLoading}
                          >
                            Delete
                          </Button>
                        </Box>
                      </Box>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 2.5, 
                          lineHeight: 1.7,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        {alert.message}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 2.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Created: {new Date(alert.createdAt).toLocaleString()}
                        </Typography>
                        {alert.biasScore > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Safety Score: {(alert.biasScore * 100).toFixed(1)}%
                          </Typography>
                        )}
                      </Box>
                      
                      <Box>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ display: 'block', mb: 1, fontWeight: 500 }}
                        >
                          Grace Period: {formatTimeRemaining(alert.gracePeriodEnds)} remaining
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={calculateGracePeriodProgress(alert.gracePeriodEnds)}
                          sx={{ height: 8, borderRadius: 4 }}
                          color="warning"
                        />
                      </Box>
                    </Box>
                    
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* All Alerts */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            {alertType === 'all' ? 'All Alerts' : `${alertType.charAt(0).toUpperCase() + alertType.slice(1)} Alerts`}
          </Typography>
          <List>
            {filteredAlerts.map((alert) => (
              <React.Fragment key={alert.id}>
                <ListItem sx={{ px: 0, py: 3, alignItems: 'flex-start', display: 'flex', gap: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                    {alert.status === 'resolved' ? (
                      <CheckCircle color="success" />
                    ) : (
                      getSeverityIcon(alert.severity)
                    )}
                  </ListItemIcon>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 'bold',
                          textDecoration: alert.status === 'resolved' ? 'line-through' : 'none',
                          opacity: alert.status === 'resolved' ? 0.7 : 1,
                        }}
                      >
                        {alert.title}
                      </Typography>
                      <Chip
                        label={alert.status}
                        color={getStatusColor(alert.status) as any}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2, 
                        lineHeight: 1.7,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {alert.message}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(alert.createdAt).toLocaleDateString()}
                        </Typography>
                        {alert.acknowledgedAt && (
                          <Typography variant="caption" color="text.secondary">
                            Acknowledged: {new Date(alert.acknowledgedAt).toLocaleDateString()}
                          </Typography>
                        )}
                        {alert.resolvedAt && (
                          <Typography variant="caption" color="success.main">
                            Resolved: {new Date(alert.resolvedAt).toLocaleDateString()}
                          </Typography>
                        )}
                        {alert.totalPenaltyApplied > 0 && (
                          <Typography variant="caption" color="error" sx={{ fontWeight: 500 }}>
                            Penalty: {alert.totalPenaltyApplied.toFixed(1)} pts
                          </Typography>
                        )}
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {(alert.status === 'unread' || alert.status === 'sent') && (
                          <Button
                            data-ga-label="Alert Acknowledge"
                            size="small"
                            variant="outlined"
                            startIcon={<Check />}
                            onClick={() => handleAcknowledge(alert.id)}
                            disabled={!canEdit || actionLoading}
                          >
                            Acknowledge
                          </Button>
                        )}
                        {(alert.status === 'unread' || alert.status === 'sent' || alert.status === 'acknowledged') && (
                          <Button
                            data-ga-label="Alert Resolve"
                            size="small"
                            variant="contained"
                            startIcon={<CheckCircle />}
                            onClick={() => handleResolve(alert)}
                            disabled={!canEdit || actionLoading}
                            sx={{ 
                              bgcolor: '#1e3a5f',
                              '&:hover': { bgcolor: '#152a4a' }
                            }}
                          >
                            Resolve
                          </Button>
                        )}
                        <Button
                          data-ga-label="Alert Delete"
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => handleDeleteClick(alert.id)}
                          disabled={!canEdit || actionLoading}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Resolve Dialog - Memoized to prevent lag when typing */}
      <ResolveDialog
        open={resolveDialogOpen}
        selectedAlert={selectedAlert}
        onClose={() => setResolveDialogOpen(false)}
        onSubmit={handleResolveSubmit}
        loading={actionLoading}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Alert</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete this alert? This will hide it from view but keep the record for tracking purposes.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            The alert will be soft-deleted and can be reviewed in the system logs if needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button data-ga-label="Delete alert Cancel" onClick={handleDeleteCancel} disabled={actionLoading}>
            Cancel
          </Button>
          <Button 
            data-ga-label="Delete alert Confirm"
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Delete Alert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Alerts;
